/**
 * One-way import of an `.ics` file into one of your own calendars (agora#159):
 * pick a file, see how many events it holds, choose the calendar, and
 * `importIcs` writes them. Nothing is written until "Import" is pressed, and
 * nothing stays linked afterwards -- the events are copies, not a
 * subscription. `@wtfalch/calendar` does not export its parser, so the
 * preview only counts the file's `VEVENT`s; what the parser then skips comes
 * back in `importIcs`'s own result and is shown once the import is done.
 */
import type {
  CalendarClient,
  Calendar as CalendarModel,
  ImportIcsResult,
} from '@wtfalch/calendar/calendar';
import { CalendarError } from '@wtfalch/calendar/calendar';
import { Button, Callout, Checkbox, Field, Icon, Modal, Select } from '@wtfalch/design';
import { useEffect, useRef, useState } from 'react';

/** Larger files are refused unread; more events than MAX_EVENTS are refused. */
export const MAX_BYTES = 2 * 1024 * 1024;
export const MAX_EVENTS = 500;
/** Above this many events the person has to tick a box before importing. */
export const CONFIRM_ABOVE = 100;

type Picked = {
  name: string;
  /** One complete calendar document per event (same-UID VEVENTs stay together). */
  units: string[];
  attendees: number;
  /** Set when nothing can be imported; the sentence says why. */
  problem?: string;
  /** Carried across a retry of the ones that did not import. */
  imported: number;
  skipped: string[];
  /** Set after a run that stopped early. */
  outcome?: string;
};

const UNCLOSED = 'has an event that is never closed, so it cannot be read.';

/** Only a calendar the person may add events to is a place to import into. */
function writable(calendars: readonly CalendarModel[]): CalendarModel[] {
  return calendars.filter((c) => c.myRights.includes('mayWriteAll'));
}

/**
 * Split a file into one calendar document per event, so each event is its own
 * request: progress can be shown, a failure says exactly what landed, and a
 * retry sends only what did not. `@wtfalch/calendar` does not export its
 * parser, so this only looks at the BEGIN/END lines. Each unit carries the
 * VCALENDAR header properties and the VTIMEZONE blocks, nothing else.
 */
function splitEvents(text: string): {
  units: string[];
  attendees: number;
  blocks: number;
  problem?: string;
} {
  const none = { units: [], attendees: 0, blocks: 0 };
  // Unfold continuation lines first, so a long UID is read in full.
  const lines = text.replace(/(?:\r\n|\n|\r)[ \t]/g, '').split(/\r\n|\n|\r/);
  if (lines.filter((l) => /^BEGIN:VCALENDAR\s*$/i.test(l)).length === 0) return none;
  if (lines.filter((l) => /^BEGIN:VCALENDAR\s*$/i.test(l)).length > 1) {
    return {
      ...none,
      problem: 'holds more than one calendar. Split it into one calendar per file.',
    };
  }
  const header: string[] = [];
  const zones: string[] = [];
  const groups = new Map<string, { lines: string[][]; attendees: boolean }>();
  let current: string[] | null = null;
  let own: string[] = []; // lines of the VEVENT itself, not of a nested VALARM
  let nested = 0;
  let zone: string[] | null = null;
  let other = 0; // depth inside a component that is not copied (VTODO, ...)
  let blocks = 0;
  let n = 0;
  for (const line of lines) {
    if (current) {
      if (/^BEGIN:VEVENT\s*$/i.test(line)) break;
      current.push(line);
      if (nested > 0) {
        if (/^BEGIN:/i.test(line)) nested += 1;
        else if (/^END:/i.test(line)) nested -= 1;
      } else if (/^BEGIN:/i.test(line)) {
        nested = 1;
      } else if (!/^END:VEVENT\s*$/i.test(line)) {
        own.push(line);
      }
      if (nested === 0 && /^END:VEVENT\s*$/i.test(line)) {
        const uid = own.find((l) => /^UID[:;]/i.test(l)) ?? `#${n++}`;
        const g = groups.get(uid) ?? { lines: [], attendees: false };
        g.lines.push(current);
        g.attendees ||= own.some((l) => /^ATTENDEE[:;]/i.test(l));
        groups.set(uid, g);
        blocks += 1;
        current = null;
      }
    } else if (zone) {
      zone.push(line);
      if (/^END:VTIMEZONE\s*$/i.test(line)) {
        zones.push(...zone);
        zone = null;
      }
    } else if (other > 0) {
      if (/^END:VCALENDAR\s*$/i.test(line)) return { ...none, problem: UNCLOSED };
      if (/^BEGIN:/i.test(line)) other += 1;
      else if (/^END:/i.test(line)) other -= 1;
    } else if (/^BEGIN:VEVENT\s*$/i.test(line)) {
      current = [line];
      own = [];
      nested = 0;
    } else if (/^BEGIN:VTIMEZONE\s*$/i.test(line)) {
      zone = [line];
    } else if (/^BEGIN:VCALENDAR\s*$/i.test(line) || /^END:VCALENDAR\s*$/i.test(line)) {
      // The wrapper is added back around each unit.
    } else if (/^BEGIN:/i.test(line)) {
      other = 1;
    } else if (line !== '') {
      header.push(line);
    }
  }
  if (current || zone || other > 0) {
    return { ...none, problem: UNCLOSED };
  }
  const units = [...groups.values()].map((g) =>
    ['BEGIN:VCALENDAR', ...header, ...zones, ...g.lines.flat(), 'END:VCALENDAR', ''].join('\r\n'),
  );
  const attendees = [...groups.values()].filter((g) => g.attendees).length;
  return { units, attendees, blocks };
}

function plural(n: number): string {
  return n === 1 ? '1 event' : `${n} events`;
}

export function ImportIcs({
  client,
  calendars,
}: {
  client: CalendarClient;
  calendars: readonly CalendarModel[];
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const stopRef = useRef(false);
  /** Bumped by every new file and every run; an older run drops its updates. */
  const runRef = useRef(0);
  const [picked, setPicked] = useState<Picked | null>(null);
  const [calendarId, setCalendarId] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [summary, setSummary] = useState<string | null>(null);
  const [stopping, setStopping] = useState(false);

  // Leaving the page stops the run, so the loop does not keep importing.
  useEffect(
    () => () => {
      stopRef.current = true;
      runRef.current += 1;
    },
    [],
  );

  const targets = writable(calendars);
  if (targets.length === 0) return null;

  const busy = progress !== null;

  // Stopping mid-import waits for the request in flight; what landed stays,
  // and the summary says how much that was.
  const close = () => {
    if (busy) {
      stopRef.current = true;
      setStopping(true);
    } else {
      setPicked(null);
    }
  };

  const pick = async (file: File) => {
    const id = ++runRef.current;
    stopRef.current = true;
    setProgress(null);
    setStopping(false);
    setSummary(null);
    setConfirmed(false);
    setCalendarId(targets[0]?.id ?? '');
    const base = { name: file.name, units: [], attendees: 0, imported: 0, skipped: [] };
    if (file.size > MAX_BYTES) {
      setPicked({
        ...base,
        problem: `${file.name} is larger than ${MAX_BYTES / 1024 / 1024} MB, which is too big to import.`,
      });
      return;
    }
    let text: string;
    try {
      text = (await file.text()).replace(/^\uFEFF/, '');
    } catch {
      if (id !== runRef.current) return;
      setPicked({ ...base, problem: `${file.name} could not be read as a calendar file.` });
      return;
    }
    if (id !== runRef.current) return;
    const { units, attendees, blocks, problem } = splitEvents(text);
    if (problem) {
      setPicked({ ...base, problem: `${file.name} ${problem}` });
    } else if (units.length === 0) {
      setPicked({
        ...base,
        problem: `${file.name} is not a calendar file with events in it, so nothing can be imported.`,
      });
    } else if (blocks > MAX_EVENTS) {
      setPicked({
        ...base,
        problem: `${file.name} holds ${blocks} events. At most ${MAX_EVENTS} can be imported at once.`,
      });
    } else {
      setPicked({ ...base, units, attendees });
    }
  };

  const run = async () => {
    if (!picked || picked.units.length === 0 || busy) return;
    stopRef.current = false;
    const id = ++runRef.current;
    const total = picked.units.length;
    let imported = picked.imported;
    const skipped = [...picked.skipped];
    let next = 0;
    let error: string | null = null;
    setProgress({ done: 0, total });
    for (; next < total && !stopRef.current && id === runRef.current; next++) {
      try {
        const unit = picked.units[next] ?? '';
        const res: ImportIcsResult = await client.importIcs(calendarId, unit);
        if (id !== runRef.current) return;
        imported += res.created.length;
        skipped.push(...res.skipped.map((x) => x.reason));
      } catch (err) {
        if (id !== runRef.current) return;
        error =
          err instanceof CalendarError && err.message !== ''
            ? err.message
            : 'The server refused it.';
        break;
      }
      setProgress({ done: next + 1, total });
    }
    if (id !== runRef.current) return;
    setProgress(null);
    setStopping(false);
    const left = total - next;
    const text =
      `Imported ${plural(imported)}.` +
      (skipped.length > 0
        ? ` ${plural(skipped.length)} could not be read and ${
            skipped.length === 1 ? 'was' : 'were'
          } skipped: ${skipped.join('; ')}.`
        : '') +
      (left > 0 ? ` ${plural(left)} did not import.` : '');
    if (error) {
      // Keep only the ones that did not land, so a retry cannot duplicate.
      setPicked({
        ...picked,
        units: picked.units.slice(next),
        imported,
        skipped,
        outcome: `${text} ${error} Retrying sends only the ${plural(left)} that did not import.`,
      });
    } else {
      setPicked(null);
      setSummary(text);
    }
  };

  const needsConfirm = picked !== null && picked.units.length > CONFIRM_ABOVE;
  const canImport =
    picked !== null &&
    picked.units.length > 0 &&
    (!needsConfirm || confirmed || picked.imported > 0);

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        hidden
        accept=".ics,text/calendar"
        aria-label="Calendar file"
        onChange={(e) => {
          const file = e.target.files?.[0] ?? null;
          e.target.value = '';
          if (file) void pick(file);
        }}
      />
      <Button kind="default" size="sm" onPress={() => inputRef.current?.click()}>
        <Icon name="upload" size={16} />
        Import
      </Button>
      {summary && (
        <div className="cal-import-summary">
          <Callout
            tone={/did not import|could not be read/.test(summary) ? 'info' : 'good'}
            onDismiss={() => setSummary(null)}
          >
            {summary}
          </Callout>
        </div>
      )}
      {picked && (
        <Modal
          title="Import a calendar file"
          onClose={close}
          footer={
            <>
              <Button kind="ghost" disabled={stopping} onPress={close}>
                {busy ? 'Stop' : 'Cancel'}
              </Button>
              <Button kind="primary" busy={busy} disabled={!canImport} onPress={() => void run()}>
                {picked.outcome ? 'Retry the rest' : 'Import'}
              </Button>
            </>
          }
        >
          <div className="cal-find-form">
            {picked.problem ? (
              <p className="cal-error text-bad" role="alert">
                {picked.problem}
              </p>
            ) : (
              <>
                <p className="text-sm">
                  {picked.name} holds {plural(picked.units.length)}. They are copied once; later
                  changes to the file do not reach this calendar.
                </p>
                {picked.attendees > 0 && (
                  <p className="text-sm" role="note">
                    {plural(picked.attendees)} in this file list attendees. They stay on the copy,
                    so deleting it later may email those people a cancellation.
                  </p>
                )}
                <Field label="Import into">
                  <Select
                    value={calendarId}
                    disabled={busy || Boolean(picked.outcome)}
                    onChange={(e) => setCalendarId(e.target.value)}
                    block
                  >
                    {targets.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </Select>
                </Field>
                {needsConfirm && picked.imported === 0 && (
                  <Checkbox
                    label={`Yes, import all ${picked.units.length} events`}
                    checked={confirmed}
                    disabled={busy}
                    onChange={setConfirmed}
                  />
                )}
              </>
            )}
            {progress && (
              <p className="text-sm" role="status">
                {stopping
                  ? 'Stopping after the current event…'
                  : `Importing ${progress.done} of ${progress.total}`}
              </p>
            )}
            {picked.outcome && !busy && (
              <p className="text-sm" role="alert">
                {picked.outcome}
              </p>
            )}
          </div>
        </Modal>
      )}
    </>
  );
}
